// platform/src/lib/diagnostic/storage.ts
//
// Persistencia del diagnóstico en Supabase.
//
// Flujo actual (v1): una sola operación de escritura por intento, al final
// del wizard. El estado del wizard se preserva localmente (localStorage)
// para sobrevivir refresh; cuando el usuario completa, se hace INSERT con
// todas las respuestas + perfil resultante + anonymous_session_id (o
// user_id si hay sesión).
//
// Cuando F2-bis añada "pausar y retomar a medio camino", se introducirán
// `startResponse` + `saveAnswers` + `markCompleted` con la RPC SECURITY
// DEFINER asociada para validar el anonymous_session_id sin exigir auth.

import { getSupabase } from '../supabase';
import type {
  DiagnosticAnswers,
  DiagnosticResponseRow,
  DiagnosticResult,
} from './types';

/** Donde guarda el navegador el identificador de sesion anonima. */
export const SESSION_KEY = 'mw-diagnostic-anon-session';

function newUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback (no usado en navegadores modernos)
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function requireClient(): NonNullable<ReturnType<typeof getSupabase>> {
  const supabase = getSupabase();
  if (!supabase) {
    throw new Error(
      '[diagnostic] Supabase no configurado (faltan env vars VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY)',
    );
  }
  return supabase;
}

/**
 * Devuelve el anonymous_session_id del navegador actual.
 * Si no existe, lo crea y guarda en localStorage. Estable entre recargas.
 *
 * Se usa para dos cosas: (1) trazar el intento anónimo en la fila de
 * diagnostic_response, y (2) reclamar esa fila tras login vía
 * `linkAnonymousResponses`.
 */
export function getOrCreateAnonymousSessionId(): string {
  if (typeof window === 'undefined') return newUuid();
  let id = window.localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = newUuid();
    window.localStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

/**
 * Marca el intento como completado. Crea la fila en diagnostic_response
 * a través de la RPC SECURITY DEFINER `complete_diagnostic_response`.
 *
 * Por qué RPC y no INSERT directo: las policies de RLS en Supabase para
 * INSERT desde rol anónimo tienen comportamientos no siempre intuitivos.
 * El patrón estándar para escrituras anónimas en Supabase es exponer una
 * función `security definer` con validaciones en SQL; es el mismo patrón
 * que ya usa `link_anonymous_diagnostic`.
 *
 * Validaciones que aplica la RPC en servidor:
 *   - tenant_id existe y status='active'
 *   - course_slug, profile_slug, answers no vacíos
 *   - owner_check: si NO hay auth.uid(), exige anonymous_session_id
 */
export async function completeResponse(
  tenantId: string,
  courseSlug: string,
  answers: DiagnosticAnswers,
  result: DiagnosticResult,
): Promise<{ id: string; profileSlug: string; completedAt: string }> {
  const supabase = requireClient();
  const {
    data: { user },
  } = await supabase.auth.getUser();

  // Si no hay sesión, generamos/recuperamos el anon_id. Si hay sesión,
  // la RPC lo ignora y usa auth.uid().
  const anonymousSessionId = user ? null : getOrCreateAnonymousSessionId();

  const { data, error } = await supabase.rpc('complete_diagnostic_response', {
    p_tenant_id: tenantId,
    p_course_slug: courseSlug,
    p_anonymous_session_id: anonymousSessionId,
    p_answers: answers,
    p_profile_slug: result.profileSlug,
    p_profile_scores: result.scores,
  });

  if (error) {
    // Fallback: si la RPC no existe, intentar INSERT directo
    console.warn('[diagnostic] RPC failed, trying direct insert:', error.message);
    const { data: fallback, error: insertErr } = await supabase
      .from('diagnostic_response')
      .insert({
        tenant_id: tenantId,
        course_slug: courseSlug,
        user_id: user?.id ?? null,
        anonymous_session_id: anonymousSessionId,
        answers,
        profile_slug: result.profileSlug,
        profile_scores: result.scores,
        started_at: new Date().toISOString(),
        completed_at: new Date().toISOString(),
      })
      .select('id, profile_slug, completed_at')
      .single();
    if (insertErr) {
      throw new Error(`[diagnostic] completeResponse fallback failed: ${insertErr.message}`);
    }
    return {
      id: fallback.id,
      profileSlug: fallback.profile_slug,
      completedAt: fallback.completed_at,
    };
  }
  // La RPC devuelve TABLE(id, profile_slug, completed_at) → array con 1 fila
  const row = Array.isArray(data) ? data[0] : data;
  if (!row?.id) {
    throw new Error('[diagnostic] completeResponse: respuesta vacía del servidor');
  }
  return {
    id: row.id,
    profileSlug: row.profile_slug,
    completedAt: row.completed_at,
  };
}

/**
 * Tras login, enlaza las respuestas anónimas del navegador actual al usuario.
 * Idempotente: se puede llamar varias veces. Devuelve cuántas filas se
 * vincularon.
 */
export async function linkAnonymousResponses(): Promise<number> {
  if (typeof window === 'undefined') return 0;
  const anonId = window.localStorage.getItem(SESSION_KEY);
  if (!anonId) return 0;

  const supabase = requireClient();
  const { data, error } = await supabase.rpc('link_anonymous_diagnostic', {
    p_anonymous_session_id: anonId,
  });
  if (error) {
    console.warn('[diagnostic] linkAnonymousResponses failed:', error.message);
    return 0;
  }
  return (data as number) ?? 0;
}

/**
 * Lee la respuesta más reciente del usuario autenticado para un curso dado.
 * Si no hay sesión, devuelve null.
 */
export async function getLatestResponseForUser(
  courseSlug: string,
): Promise<DiagnosticResponseRow | null> {
  const supabase = requireClient();
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('diagnostic_response')
    .select('*')
    .eq('user_id', user.id)
    .eq('course_slug', courseSlug)
    .not('completed_at', 'is', null)
    .order('completed_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.warn('[diagnostic] getLatestResponseForUser failed:', error.message);
    return null;
  }
  return (data as DiagnosticResponseRow) ?? null;
}
